import { useEffect, useState } from "react";
import type { Car } from "@/types/database";
import { AdminPasswordConfirm } from "@/components/auth/AdminPasswordConfirm";
import { useAuth } from "@/contexts/AuthContext";
import { useUpdateCar } from "@/lib/queries";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";

function formFromCar(car: Car) {
  return {
    make: car.make ?? "",
    model: car.model ?? "",
    modelYear: car.modelYear?.toString() ?? "",
    trim: car.trim ?? "",
    series: car.series ?? "",
    color: car.color ?? "",
    vin: car.vin ?? "",
    body: car.body ?? "",
    transmission: car.transmission ?? "",
    drive: car.drive ?? "",
    fuelTypePrimary: car.fuelTypePrimary ?? "",
    enginePowerHP: car.enginePowerHP?.toString() ?? "",
    numberOfDoors: car.numberOfDoors?.toString() ?? "",
    numberOfSeats: car.numberOfSeats ?? "",
    fuelCapacity: car.fuelCapacity?.toString() ?? "",
    maxSpeed: car.maxSpeed?.toString() ?? "",
    licensePlate: car.licensePlate ?? "",
    odometer: car.odometer?.toString() ?? "",
    value: car.value?.toString() ?? "",
    shiftRating: car.shiftRating?.toString() ?? "",
    ownersRating: car.ownersRating?.toString() ?? "",
  };
}

function parseNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseInteger(value: string): number | null {
  const parsed = parseNumber(value);
  if (parsed == null) return null;
  return Number.isInteger(parsed) ? parsed : Math.trunc(parsed);
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted">{title}</h3>
      {children}
    </section>
  );
}

export function EditCarModal({
  car,
  open,
  onClose,
}: {
  car: Car;
  open: boolean;
  onClose: () => void;
}) {
  const { verifyPassword } = useAuth();
  const updateCarMutation = useUpdateCar();
  const [form, setForm] = useState(() => formFromCar(car));
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const saving = updateCarMutation.isPending;
  const pending = saving || verifying;

  useEffect(() => {
    if (!open) return;
    setForm(formFromCar(car));
    setPassword("");
    setError(null);
  }, [open, car]);

  function closeModal() {
    if (pending) return;
    onClose();
  }

  function updateField<K extends keyof ReturnType<typeof formFromCar>>(key: K, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSave() {
    const make = form.make.trim();
    const model = form.model.trim();
    if (!make) {
      setError("Make is required.");
      return;
    }
    if (!model) {
      setError("Model is required.");
      return;
    }
    if (!password.trim()) {
      setError("Enter your password to confirm this change.");
      return;
    }

    setError(null);
    setVerifying(true);
    try {
      const verifyError = await verifyPassword(password);
      if (verifyError) {
        setError(verifyError);
        return;
      }

      await updateCarMutation.mutateAsync({
        carId: car.carID,
        updates: {
          make,
          model,
          modelYear: parseInteger(form.modelYear),
          trim: form.trim.trim() || null,
          series: form.series.trim() || null,
          color: form.color.trim() || null,
          vin: form.vin.trim() || null,
          body: form.body.trim() || null,
          transmission: form.transmission.trim() || null,
          drive: form.drive.trim() || null,
          fuelTypePrimary: form.fuelTypePrimary.trim() || null,
          enginePowerHP: parseNumber(form.enginePowerHP),
          numberOfDoors: parseInteger(form.numberOfDoors),
          numberOfSeats: form.numberOfSeats.trim() || null,
          fuelCapacity: parseNumber(form.fuelCapacity),
          maxSpeed: parseNumber(form.maxSpeed),
          licensePlate: form.licensePlate.trim() || null,
          odometer: parseNumber(form.odometer),
          value: parseNumber(form.value),
          shiftRating: parseNumber(form.shiftRating),
          ownersRating: parseNumber(form.ownersRating),
        },
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setVerifying(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title="Edit car record"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={closeModal} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleSave}
            disabled={pending || !password.trim() || !form.make.trim() || !form.model.trim()}
          >
            {pending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      {error ? <p className="mb-4 rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p> : null}
      <div className="space-y-6 text-foreground">
        <FormSection title="Vehicle">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Make">
              <Input value={form.make} onChange={(e) => updateField("make", e.target.value)} autoFocus />
            </Field>
            <Field label="Model">
              <Input value={form.model} onChange={(e) => updateField("model", e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Year">
              <Input type="number" value={form.modelYear} onChange={(e) => updateField("modelYear", e.target.value)} />
            </Field>
            <Field label="Color">
              <Input value={form.color} onChange={(e) => updateField("color", e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Trim">
              <Input value={form.trim} onChange={(e) => updateField("trim", e.target.value)} />
            </Field>
            <Field label="Series">
              <Input value={form.series} onChange={(e) => updateField("series", e.target.value)} />
            </Field>
          </div>
          <Field label="VIN">
            <Input value={form.vin} onChange={(e) => updateField("vin", e.target.value)} />
          </Field>
        </FormSection>

        <FormSection title="Specifications">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Body">
              <Input value={form.body} onChange={(e) => updateField("body", e.target.value)} />
            </Field>
            <Field label="Transmission">
              <Input value={form.transmission} onChange={(e) => updateField("transmission", e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Drive">
              <Input value={form.drive} onChange={(e) => updateField("drive", e.target.value)} />
            </Field>
            <Field label="Fuel type">
              <Input value={form.fuelTypePrimary} onChange={(e) => updateField("fuelTypePrimary", e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Engine (HP)">
              <Input type="number" value={form.enginePowerHP} onChange={(e) => updateField("enginePowerHP", e.target.value)} />
            </Field>
            <Field label="Max speed">
              <Input type="number" value={form.maxSpeed} onChange={(e) => updateField("maxSpeed", e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Doors">
              <Input type="number" value={form.numberOfDoors} onChange={(e) => updateField("numberOfDoors", e.target.value)} />
            </Field>
            <Field label="Seats">
              <Input value={form.numberOfSeats} onChange={(e) => updateField("numberOfSeats", e.target.value)} />
            </Field>
            <Field label="Fuel capacity">
              <Input type="number" value={form.fuelCapacity} onChange={(e) => updateField("fuelCapacity", e.target.value)} />
            </Field>
          </div>
        </FormSection>

        <FormSection title="Registration & ratings">
          <Field label="License plate">
            <Input value={form.licensePlate} onChange={(e) => updateField("licensePlate", e.target.value)} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Odometer">
              <Input type="number" value={form.odometer} onChange={(e) => updateField("odometer", e.target.value)} />
            </Field>
            <Field label="Value (JMD)">
              <Input type="number" value={form.value} onChange={(e) => updateField("value", e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Shift rating">
              <Input type="number" step="0.1" value={form.shiftRating} onChange={(e) => updateField("shiftRating", e.target.value)} />
            </Field>
            <Field label="Owner rating">
              <Input type="number" step="0.1" value={form.ownersRating} onChange={(e) => updateField("ownersRating", e.target.value)} />
            </Field>
          </div>
        </FormSection>

        <AdminPasswordConfirm password={password} onPasswordChange={setPassword} disabled={pending} />
      </div>
    </Modal>
  );
}
